import { t as tr } from "../../../i18n";
import type { ReactNode } from "react";
import { fmt, type CheckResult, type WallResult } from "@tasduvar/engine";

export interface InputRow { label: string; symbol?: string; value: string | number; unit?: string; digits?: number }
export function InputTable({ title, rows }: { title?: string; rows: InputRow[] }): JSX.Element {
  return (
    <>
      {title ? <h3>{title}</h3> : null}
      <table>
        <thead><tr><th style={{ width: "46%" }}>{tr("Açıklama")}</th><th>{tr("Simge")}</th><th className="num">{tr("Değer")}</th><th>{tr("Birim")}</th></tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}><td>{r.label}</td><td className="mono">{r.symbol ?? ""}</td><td className={typeof r.value === "string" && r.value.length > 24 ? "text" : "num"}>{typeof r.value === "number" ? fmt(r.value, r.digits ?? 2) : r.value}</td><td>{r.unit ?? ""}</td></tr>)}</tbody>
      </table>
    </>
  );
}

export const Verdict = ({ ok }: { ok: boolean }): JSX.Element => <span className={ok ? "ok" : "bad"}>{ok ? tr("✓ SAĞLIYOR") : tr("✗ SAĞLAMIYOR")}</span>;

/** Hüküm vermeyen (bilgi amaçlı) klasik GK — TBDY, classicGoverns kapalı: SAĞLAMIYOR yerine gri "bilgi" */
export const InfoVerdict = (): JSX.Element => <span className="muted" data-informative="true">{tr("bilgi")}</span>;
/** Klasik sonuç hücresi: bilgi amaçlıysa InfoVerdict, değilse SAĞLIYOR/SAĞLAMIYOR */
export const ClassicVerdict = ({ check }: { check: CheckResult }): JSX.Element => check.classic.informative ? <InfoVerdict /> : <Verdict ok={check.classic.ok} />;

/** Klasik özet (devrilme, kayma, taşıma, e): bilgi amaçlı GK sayılmaz; hepsi bilgi amaçlıysa "info" */
export function classicSummary(result: WallResult): boolean | "info" {
  const cases = Object.values(result.cases).filter((x): x is NonNullable<typeof x> => !!x);
  const all = cases.flatMap((x) => [x.overturning, x.sliding, x.bearing]);
  const ruled = all.filter((c) => !c.classic.informative);
  if (ruled.length === 0) return "info";
  return ruled.every((c) => c.classic.ok) && cases.every((x) => x.eccentricity.ok);
}

export interface CheckRowData { label: string; check: CheckResult; unit: string }

/** Kontrol tablosu — spec §8.2: Durum | Önleyici | Etki | Katsayı | Hedef | Sonuç. */
export function CheckTable({ caseLabel, rows, mode }: { caseLabel: string; rows: CheckRowData[]; mode: "tbdy" | "classic" }): JSX.Element {
  return (
    <table>
      <thead><tr><th>{tr("Durum:")} {caseLabel}</th><th className="num">{mode === "tbdy" ? tr("Direnç (tasarım)") : tr("Önleyici")}</th><th className="num">{tr("Etki")}</th><th className="num">{mode === "tbdy" ? tr("Oran") : "GK"}</th><th className="num">{mode === "tbdy" ? "γR" : tr("Hedef")}</th><th>{tr("Sonuç")}</th></tr></thead>
      <tbody>
        {rows.map((r) => {
          if (mode === "tbdy") {
            const t = r.check.tbdy;
            if (!t) return <tr key={r.label}><td>{r.label}</td><td colSpan={5} className="muted">{tr("Yönetmelik kuralı yok (yalnız GK)")}</td></tr>;
            return <tr key={r.label}><td>{r.label}</td><td className="num">{fmt(t.designResistance)} {r.unit}</td><td className="num">{fmt(t.effect)} {r.unit}</td><td className="num">{fmt(t.ratio, 3)}</td><td className="num">{fmt(t.gammaR, 2)}</td><td><Verdict ok={t.ok} /></td></tr>;
          }
          const c = r.check.classic;
          return <tr key={r.label}><td>{r.label}</td><td className="num">{fmt(c.resistance)} {r.unit}</td><td className="num">{fmt(c.effect)} {r.unit}</td><td className="num">{Number.isFinite(c.factor) ? fmt(c.factor, 2) : "∞"}</td><td className="num">{fmt(c.target, 2)}</td><td><ClassicVerdict check={r.check} /></td></tr>;
        })}
      </tbody>
    </table>
  );
}

export function Section({ id, title, children }: { id: string; title: string; children: ReactNode }): JSX.Element {
  return <div data-block={id}><h2>{title}</h2>{children}</div>;
}

export const deg = (v: number): string => `${fmt(v, 1)}°`;
